import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useMemo } from "react"

interface ActivityHeatmapGridProps {
    data: Record<string, number>
    days: number
    label?: string
}

const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""]
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function getColorLevel(count: number, thresholds: number[]): number {
    if (count === 0) return 0
    if (count <= thresholds[0]) return 1
    if (count <= thresholds[1]) return 2
    if (count <= thresholds[2]) return 3
    return 4
}

const LEVEL_CLASSES = [
    "bg-muted",
    "bg-green-200 dark:bg-green-900",
    "bg-green-400 dark:bg-green-700",
    "bg-green-500 dark:bg-green-500",
    "bg-green-700 dark:bg-green-400",
]

export function ActivityHeatmapGrid({ data, days, label = "actions" }: ActivityHeatmapGridProps) {
    const { grid, thresholds, monthLabels } = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const dates: { date: Date; dateStr: string; count: number }[] = []
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(today)
            d.setDate(d.getDate() - i)
            const dateStr = d.toISOString().slice(0, 10)
            dates.push({ date: d, dateStr, count: data[dateStr] || 0 })
        }

        // Pad start to align with Sunday (column 0)
        const firstDay = dates[0]?.date.getDay() ?? 0
        const padded: (typeof dates[0] | null)[] = Array(firstDay).fill(null).concat(dates)

        // Arrange into weeks (columns of 7 days)
        const weekCols: (typeof dates[0] | null)[][] = []
        for (let i = 0; i < padded.length; i += 7) {
            weekCols.push(padded.slice(i, i + 7))
        }
        // Pad last week to 7
        const lastWeek = weekCols[weekCols.length - 1]
        if (lastWeek && lastWeek.length < 7) {
            while (lastWeek.length < 7) lastWeek.push(null)
        }

        // Quartile thresholds of non-zero values
        const nonZero = dates.map(d => d.count).filter(c => c > 0).sort((a, b) => a - b)
        let thresh = [1, 2, 3]
        if (nonZero.length > 0) {
            const q1 = nonZero[Math.floor(nonZero.length * 0.25)] || 1
            const q2 = nonZero[Math.floor(nonZero.length * 0.5)] || 2
            const q3 = nonZero[Math.floor(nonZero.length * 0.75)] || 3
            thresh = [q1, q2, q3]
        }

        // Month labels at first week of each month
        const mLabels: { label: string; col: number }[] = []
        let lastMonth = -1
        weekCols.forEach((week, colIdx) => {
            for (const cell of week) {
                if (cell) {
                    const m = cell.date.getMonth()
                    if (m !== lastMonth) {
                        mLabels.push({ label: MONTH_LABELS[m], col: colIdx })
                        lastMonth = m
                        break
                    }
                }
            }
        })

        return { grid: weekCols, thresholds: thresh, monthLabels: mLabels }
    }, [data, days])

    return (
        <div className="space-y-1">
            <div className="flex gap-0.5">
                {/* Day labels */}
                <div className="flex flex-col gap-0.5 text-[9px] text-muted-foreground pr-1 pt-3">
                    {DAY_LABELS.map((dayLabel, i) => (
                        <div key={i} className="h-[11px] leading-[11px]">{dayLabel}</div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex gap-0.5 relative pt-3">
                    {/* Month labels (absolute positioned above grid) */}
                    <div className="absolute -top-0.5 left-0 flex">
                        {monthLabels.map((m, i) => (
                            <span
                                key={i}
                                className="text-[9px] text-muted-foreground absolute"
                                style={{ left: `${m.col * 13}px` }}
                            >
                                {m.label}
                            </span>
                        ))}
                    </div>

                    {grid.map((week, colIdx) => (
                        <div key={colIdx} className="flex flex-col gap-0.5">
                            {week.map((cell, rowIdx) => {
                                if (!cell) {
                                    return <div key={rowIdx} className="w-[11px] h-[11px]" />
                                }
                                const level = getColorLevel(cell.count, thresholds)
                                const dateLabel = cell.date.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                })
                                return (
                                    <Tooltip key={rowIdx}>
                                        <TooltipTrigger asChild>
                                            <div
                                                className={cn(
                                                    "w-[11px] h-[11px] rounded-[2px] transition-colors",
                                                    LEVEL_CLASSES[level]
                                                )}
                                            />
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs">
                                            <span className="font-medium">{cell.count}</span> {label} on {dateLabel}
                                        </TooltipContent>
                                    </Tooltip>
                                )
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-1 justify-end text-[9px] text-muted-foreground">
                <span>Less</span>
                {LEVEL_CLASSES.map((cls, i) => (
                    <div key={i} className={cn("w-[11px] h-[11px] rounded-[2px]", cls)} />
                ))}
                <span>More</span>
            </div>
        </div>
    )
}
